// Dialog (HANDOFF §1): 440 px, radius 22 — ONLY for account-level actions and
// refusals (closed period, 409). Never for routine errors (those are an
// ErrorCard in the content). Full screen under 768 px. The dialog owns the
// coral while open (its decisive action is a <PrimaryAction>).

import { useEffect, useId, useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { OverlayDepthContext, overlayStore, useOverlayDepthOf } from './overlay-store';

export type DialogTone = 'danger' | 'info' | 'neutral';

export interface DialogProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  /** 40 px tinted icon tile beside the title (e.g. <Lock/> on the closed-period refusal). */
  icon?: ReactNode;
  tone?: DialogTone;
  children?: ReactNode;
  /** Buttons, stacked: decisive action first, then the line/ghost exits. */
  actions?: ReactNode;
  /** Clicking the scrim closes (default true). */
  dismissible?: boolean;
  'data-testid'?: string;
}

export function Dialog(props: DialogProps) {
  if (!props.open) return null;
  return <OpenDialog {...props} />;
}

function OpenDialog({ title, onClose, icon, tone = 'neutral', children, actions, dismissible = true, 'data-testid': testId }: DialogProps) {
  const id = useId();
  const depth = useOverlayDepthOf(id);
  const boxRef = useRef<HTMLDivElement>(null);
  // onClose kept in a ref: an inline onClose must not re-run the focus effect
  // on every parent render (it restored focus, then stole it back — a textarea
  // in a dialog lost focus after the first keystroke).
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useLayoutEffect(() => {
    overlayStore.push(id, 'dialog');
    return () => overlayStore.remove(id);
  }, [id]);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    boxRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && overlayStore.depthOf(id) === overlayStore.depth()) onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [id]);

  return createPortal(
    <OverlayDepthContext.Provider value={depth}>
      <div className="e-dialog-scrim" onClick={dismissible ? onClose : undefined}>
        <div
          ref={boxRef}
          className="e-dialog"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={`${id}-t`}
          tabIndex={-1}
          data-tone={tone}
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="e-dialog-head">
            {icon && <span className="e-dialog-icon">{icon}</span>}
            <h2 id={`${id}-t`} className="e-dialog-title">
              {title}
            </h2>
          </div>
          {children && <div className="e-dialog-body">{children}</div>}
          {actions && <div className="e-dialog-actions">{actions}</div>}
        </div>
      </div>
    </OverlayDepthContext.Provider>,
    document.body,
  );
}
